import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { OpportunitiesShell } from "@/components/CandidateHeader";
import { CompanyLogo } from "@/pages/candidate/Companies";
import { Briefcase, MapPin, Loader2, Power, Sparkles, Building2, ChevronRight, PowerOff, Target, Check, TriangleAlert, Send, CheckCircle2, X, Quote } from "lucide-react";

function matchColor(s) {
  if (s >= 70) return "bg-emerald-50 border-emerald-200 text-emerald-700";
  if (s >= 40) return "bg-amber-50 border-amber-200 text-amber-700";
  return "bg-slate-100 border-slate-200 text-slate-600";
}

const DIM_LABELS = { skills: "Zručnosti", experience: "Skúsenosti", seniority: "Seniorita", location: "Lokalita", work_mode: "Pracovný režim" };
const DIM_ORDER = ["skills", "experience", "seniority", "location", "work_mode"];

function barColor(v) { return v >= 70 ? "bg-emerald-500" : v >= 40 ? "bg-amber-500" : "bg-slate-400"; }

export function MatchBreakdown({ j }) {
  const b = j.breakdown || {};
  return (
    <div className="mt-3 rounded-2xl bg-slate-50 border border-slate-100 p-3" data-testid={`job-breakdown-${j.id}`}>
      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700"><Target size={12} className="text-blue-700" /> Detail zhody</div>
      <div className="mt-2 space-y-1.5">
        {DIM_ORDER.filter((k) => typeof b[k] === "number").map((k) => (
          <div key={k} data-testid={`dim-${k}-${j.id}`}>
            <div className="flex justify-between text-[11px] mb-0.5"><span className="text-slate-600 font-medium">{DIM_LABELS[k]}</span><span className="text-slate-400">{b[k]}%</span></div>
            <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full rounded-full ${barColor(b[k])}`} style={{ width: `${b[k]}%` }} /></div>
          </div>
        ))}
      </div>
      {j.strengths?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] font-bold text-slate-700 mb-1">Silné stránky</div>
          <ul className="space-y-1">
            {j.strengths.map((s, i) => <li key={i} className="flex gap-1.5 text-xs text-slate-600"><Check size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" /><span>{s}</span></li>)}
          </ul>
        </div>
      )}
      {j.skill_gaps?.length > 0 && (
        <div className="mt-3" data-testid={`job-gaps-${j.id}`}>
          <div className="text-[11px] font-bold text-slate-700 mb-1 flex items-center gap-1"><TriangleAlert size={12} className="text-amber-500" /> Chýbajúce zručnosti</div>
          <div className="flex flex-wrap gap-1.5">
            {j.skill_gaps.map((s, i) => <span key={i} className="rounded-full bg-amber-50 border border-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">{s}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function ApplyDialog({ j, onClose, onApplied }) {
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const company = j.company_name || "spoločnosť";
  const consentText = `Súhlasím so spracovaním mojich osobných údajov spoločnosťou ${company} za účelom posúdenia mojej žiadosti o pracovnú pozíciu ${j.title}. Beriem na vedomie, že poskytnutie súhlasu je dobrovoľné a môžem ho kedykoľvek odvolať.`;

  const submit = async () => {
    if (!consent) { toast.error("Bez GDPR súhlasu nie je možné reakciu odoslať."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/candidate/jobs/${j.id}/apply`, { consent: true });
      setDone(data.quote || "Vaša reakcia bola úspešne odoslaná.");
      onApplied();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 409) { onApplied(); onClose(); }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="apply-dialog" onClick={onClose}>
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-4" data-testid="apply-success">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100"><CheckCircle2 className="text-emerald-600" size={32} /></div>
            <h3 className="mt-4 font-head text-xl font-bold text-slate-900">Reakcia odoslaná!</h3>
            <p className="mt-1 text-sm text-slate-500">Váš profil bol úspešne odoslaný spoločnosti {company}.</p>
            <div className="mt-4 flex items-start gap-2 rounded-2xl bg-blue-50 border border-blue-100 p-4 text-left">
              <Quote size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900 italic" data-testid="apply-quote">{done}</p>
            </div>
            <button data-testid="apply-close-btn" onClick={onClose} className="mt-5 w-full bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 text-sm font-semibold transition-colors active:scale-95">Zavrieť</button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <h3 className="font-head text-lg font-bold text-slate-900">Reagovať na pozíciu</h3>
              <button data-testid="apply-dialog-close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <p className="mt-1 text-sm text-slate-600"><span className="font-semibold">{j.title}</span>{company ? ` · ${company}` : ""}</p>
            <p className="mt-3 text-sm text-slate-600">Po odoslaní bude váš profil sprístupnený spoločnosti na posúdenie žiadosti o túto pozíciu.</p>
            <label className="mt-4 flex items-start gap-3 cursor-pointer rounded-2xl border border-slate-200 p-4 hover:bg-slate-50 transition-colors" data-testid="apply-consent-label">
              <input type="checkbox" data-testid="apply-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-5 w-5 rounded border-slate-300 text-blue-700 focus:ring-blue-600" />
              <span className="text-xs leading-relaxed text-slate-700">{consentText}</span>
            </label>
            <div className="mt-5 flex gap-3">
              <button data-testid="apply-cancel-btn" onClick={onClose} className="flex-1 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Zrušiť</button>
              <button data-testid="apply-submit-btn" onClick={submit} disabled={!consent || submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? <><Loader2 className="animate-spin" size={16} /> Odosielam…</> : <><Send size={16} /> Odoslať reakciu</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function JobCard({ j, showCompany = true }) {
  const hasScore = typeof j.match_score === "number";
  const [applied, setApplied] = useState(!!j.applied);
  const [dialog, setDialog] = useState(false);
  useEffect(() => { setApplied(!!j.applied); }, [j.applied]);
  const canApply = !!j.company_id;
  return (
    <div data-testid={`job-card-${j.id}`} className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        {showCompany && <CompanyLogo logo={j.company_logo} name={j.company_name} size={44} />}
        <div className="min-w-0 flex-1">
          <h3 className="font-head font-semibold text-lg leading-snug">{j.title}</h3>
          {showCompany && j.company_name && <p className="text-sm text-slate-500 truncate">{j.company_name}</p>}
        </div>
        {hasScore ? (
          <span data-testid={`job-match-${j.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${matchColor(j.match_score)}`}>
            <Target size={12} /> Zhoda {j.match_score}%
          </span>
        ) : j.relevance > 0 ? (
          <span data-testid={`job-relevant-${j.id}`} className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-700"><Sparkles size={11} /> Pre teba</span>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
        {j.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {j.location}</span>}
        {j.employment_type && <span className="rounded-full bg-slate-100 px-2 py-0.5">{j.employment_type}</span>}
        {j.salary && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-medium">{j.salary}</span>}
      </div>
      {j.description && <p className="mt-3 text-sm text-slate-600 line-clamp-3">{j.description}</p>}
      {hasScore && <MatchBreakdown j={j} />}
      {j.required_skills?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {j.required_skills.slice(0, 5).map((s, i) => (
            <span key={i} className="rounded-full bg-blue-50 border border-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">{s}</span>
          ))}
        </div>
      )}
      {canApply && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          {applied ? (
            <div data-testid={`job-applied-${j.id}`} className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-700">
              <CheckCircle2 size={16} /> Reagovali ste
            </div>
          ) : (
            <button data-testid={`job-apply-btn-${j.id}`} onClick={() => setDialog(true)}
              className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors active:scale-95">
              <Send size={16} /> Mám záujem
            </button>
          )}
        </div>
      )}
      {dialog && <ApplyDialog j={j} onClose={() => setDialog(false)} onApplied={() => setApplied(true)} />}
    </div>
  );
}

function CompaniesSidebar() {
  const nav = useNavigate();
  const [companies, setCompanies] = useState(null);
  useEffect(() => { api.get("/candidate/companies").then((r) => setCompanies(r.data.companies)).catch(() => setCompanies([])); }, []);
  return (
    <aside className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="jobs-firmy-sidebar">
      <div className="flex items-center justify-between">
        <h2 className="font-head font-semibold text-lg flex items-center gap-2"><Building2 size={18} className="text-blue-700" /> Firmy</h2>
        <button data-testid="see-all-companies" onClick={() => nav("/app/companies")} className="text-xs font-semibold text-blue-700 hover:underline">Zobraziť všetky</button>
      </div>
      {companies === null ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-700" size={20} /></div>
      ) : companies.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Zatiaľ žiadne firmy.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {companies.slice(0, 6).map((c) => (
            <button key={c.id} data-testid={`sidebar-company-${c.id}`} onClick={() => nav(`/app/companies/${c.id}`)}
              className="w-full text-left flex items-center gap-3 rounded-2xl p-2 hover:bg-slate-50 transition-colors">
              <CompanyLogo logo={c.logo} name={c.name} size={38} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                <div className="text-xs text-slate-500">{c.open_jobs_count} {c.open_jobs_count === 1 ? "ponuka" : "ponúk"}</div>
              </div>
              <ChevronRight className="text-slate-300" size={16} />
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}

export default function CandidateJobs() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/candidate/jobs").then((r) => setData(r.data)).catch(() => setData({ open_to_work: false, jobs: [] })); }, []);

  return (
    <OpportunitiesShell>
      <h1 className="font-head text-3xl font-bold tracking-tight">Ponuky pre teba</h1>
      <p className="mt-2 text-slate-600">Verejné pracovné ponuky zoradené podľa zhody s tvojím profilom.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          {data === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div>
          ) : !data.open_to_work ? (
            <div className="rounded-3xl bg-amber-50 border border-amber-200 p-8 text-center" data-testid="not-open-prompt">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100"><PowerOff className="text-amber-600" /></div>
              <p className="mt-4 font-head font-semibold text-lg text-amber-900">Nie ste otvorený pracovným príležitostiam</p>
              <p className="mt-1 text-sm text-amber-800">Zapnite v profile prepínač „Som otvorený pracovným príležitostiam", aby sa vám tu zobrazili verejné ponuky.</p>
              <button data-testid="go-enable-btn" onClick={() => nav("/app/profile")}
                className="mt-4 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 text-sm font-semibold transition-colors active:scale-95">
                <Power size={16} /> Prejsť do profilu
              </button>
            </div>
          ) : data.jobs.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="jobs-empty">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50"><Briefcase className="text-blue-700" /></div>
              <p className="mt-4 font-semibold text-slate-800">Zatiaľ žiadne ponuky</p>
              <p className="text-sm text-slate-500 mt-1">Momentálne nie sú publikované žiadne verejné pracovné ponuky.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="jobs-for-you-list">
              {data.jobs.map((j) => <JobCard key={j.id} j={j} />)}
            </div>
          )}
        </div>
        <CompaniesSidebar />
      </div>
    </OpportunitiesShell>
  );
}
